import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { BlackCard } from '../components/cards/BlackCard';
import { MaterialIcon } from '../components/MaterialIcon';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { useGame } from '../state/gameContext';
import { wildCardsInSelection } from '../state/cardSelection';

export default function WriteInModalPage() {
  const { state, setWriteIn, confirmWriteIn, runtimeMode } = useGame();
  const navigate = useNavigate();
  const [validationError, setValidationError] = useState(false);
  const maxLength = state.writeInMaxLength ?? 60;

  if (!state.roomCode) {
    return <Navigate to="/create" replace />;
  }

  const wildCount = wildCardsInSelection(state.selectedCardIds, state.hand).length;
  if (wildCount === 0) {
    return <Navigate to="/game" replace />;
  }

  const handleConfirm = () => {
    if (!state.writeInText.trim()) {
      setValidationError(true);
      return;
    }
    const ok = confirmWriteIn();
    if (!ok) {
      setValidationError(true);
      return;
    }
    // Mock mode navigates in the store. In live mode the route effect intentionally
    // keeps the player on /write-in (so the server snapshot can't yank them away
    // mid-typing), so once the submission is accepted we navigate back to the table
    // ourselves to show the "submitted, waiting" state.
    if (runtimeMode === 'server') {
      navigate('/game');
    }
  };

  return (
    <div className="bg-background text-on-background font-body-lg overflow-hidden min-h-screen">
      <div className="fixed inset-0 z-0 overflow-hidden select-none pointer-events-none grayscale-[0.2] brightness-[0.4] blur-md">
        <div className="absolute inset-0 flex flex-col items-center justify-center space-y-12 p-10">
          <BlackCard text={state.blackCardText ?? 'Write your answer for _________.'} size="sm" className="shadow-2xl" />
        </div>
      </div>

      <div className="fixed inset-0 z-50 flex items-center justify-center p-margin-mobile md:p-margin-desktop backdrop-blur-xl bg-black/40">
        <div className="relative w-full max-w-xl bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col">
          <div className="px-stack-lg pt-stack-lg pb-stack-sm flex justify-between items-start">
            <h1 className="font-headline-md text-headline-md text-black tracking-tight leading-tight">
              Write your own answer
            </h1>
            <Link to="/game" className="p-2 hover:bg-black/5 rounded-full transition-colors text-black/40" aria-label="Close">
              <MaterialIcon name="close" className="text-3xl" />
            </Link>
          </div>
          <div className="px-stack-lg py-stack-md flex-grow flex flex-col">
            {wildCount > 1 && (
              <p className="text-label-sm text-black/60 mb-stack-sm">
                This submission includes {wildCount} wild cards — the same custom text will fill each wild slot.
              </p>
            )}
            <div className="relative group card-glow rounded-xl overflow-hidden border-2 border-transparent transition-all">
              <div className="absolute inset-0 p-[2px] rounded-xl bg-gradient-to-tr from-primary via-secondary to-tertiary opacity-40 group-focus-within:opacity-100 transition-opacity" />
              <div className="relative bg-white rounded-[10px] p-1">
                <textarea
                  value={state.writeInText}
                  onChange={(e) => {
                    setWriteIn(e.target.value);
                    setValidationError(false);
                  }}
                  data-testid="write-in-input"
                  className="w-full min-h-[180px] bg-transparent border-none focus:ring-0 text-black font-card-text text-card-text resize-none placeholder:text-black/20"
                  placeholder="Type your custom answer..."
                  aria-label="Custom answer text"
                  maxLength={maxLength}
                />
              </div>
            </div>
            <div className="mt-stack-sm flex justify-between items-center">
              <p
                data-testid="write-in-validation"
                data-error={validationError ? 'true' : 'false'}
                className={`font-label-bold text-label-bold flex items-center gap-1 ${validationError ? 'text-error' : 'text-black/50'}`}
              >
                <MaterialIcon name="warning" className="text-[18px]" />
                {validationError ? 'Enter an answer before continuing.' : 'Keep it short and readable.'}
              </p>
              <div className="font-label-bold text-label-bold text-black/40">
                <span className="text-black">{state.writeInText.length}</span>/{maxLength}
              </div>
            </div>
          </div>
          <div className="p-stack-lg bg-surface-container-lowest border-t border-black/5 flex flex-col sm:flex-row gap-stack-md">
            <Link
              to="/game"
              className="flex-1 px-8 py-4 rounded-full border-2 border-black text-black font-label-bold text-label-bold hover:bg-black hover:text-white transition-all active:scale-95 text-center"
            >
              Cancel
            </Link>
            <button
              type="button"
              onClick={handleConfirm}
              data-testid="confirm-write-in"
              className="flex-1 px-8 py-4 rounded-full bg-primary-container text-on-primary-container font-label-bold text-label-bold shadow-lg hover:-translate-y-0.5 transition-all active:scale-95"
            >
              Use This Answer
            </button>
          </div>
        </div>
      </div>

      <TopNav />
      <MobileBottomNav active="/game" />
    </div>
  );
}
